import { useCallback } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ReservationsClient } from "@/features/reservations/ReservationsClient";
import {
  listResources,
  listReservations,
  listMyCalendar,
  reservationKeys,
  type ReservationsSearch,
} from "@/features/reservations/reservations.api";
import { applyParamUpdates } from "@/lib/searchParams";
import {
  parseLocal,
  toLocalIso,
  startOfWeekMonday,
  endOfWeekMonday,
  startOfMonth,
  endOfMonth,
  addDays,
  setTime,
} from "@/lib/dates";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { isDefaultFilter, readReservationFilterPrefs } from "@/features/reservations/reservationFilters";

export const Route = createFileRoute("/_shell/reservations/")({
  beforeLoad: ({ context, search }) => {
    if (!hasModule(context.session, "reservations")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
    // Arriving with no filter in the link (the menu, a bookmark): the rooms and
    // filters this person last left the board with, instead of every room
    // (Camiel, 2026-09-17). A link that names a filter keeps it.
    if (!search.resourceIds && !search.branch && !search.type) {
      const kept = readReservationFilterPrefs();
      if (kept && !isDefaultFilter(kept)) {
        throw redirect({
          to: "/reservations",
          search: {
            ...search,
            ...(kept.resourceIds ? { resourceIds: kept.resourceIds } : {}),
            ...(kept.branch ? { branch: kept.branch } : {}),
            ...(kept.type ? { type: kept.type } : {}),
          },
          replace: true,
        });
      }
    }
  },
  validateSearch: (search: Record<string, unknown>): ReservationsSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      type: str(search.type),
      branch: str(search.branch),
      resourceIds: str(search.resourceIds),
      view: str(search.view),
      date: str(search.date),
      mine: str(search.mine),
      outlook: str(search.outlook),
      open: str(search.open),
      openResource: str(search.openResource),
      suggest: str(search.suggest),
    };
  },
  component: ReservationsScreen,
});

function computeRange(view: string, anchor: Date): { from: string; to: string } {
  if (view === "month") {
    return {
      from: toLocalIso(startOfWeekMonday(startOfMonth(anchor))),
      to: toLocalIso(endOfWeekMonday(endOfMonth(anchor))),
    };
  }
  if (view === "day") {
    return { from: toLocalIso(setTime(anchor, 0, 0)), to: toLocalIso(setTime(anchor, 23, 59)) };
  }
  if (view === "agenda") {
    return { from: toLocalIso(setTime(anchor, 0, 0)), to: toLocalIso(setTime(addDays(anchor, 30), 23, 59)) };
  }
  // week (default)
  return { from: toLocalIso(startOfWeekMonday(anchor)), to: toLocalIso(endOfWeekMonday(anchor)) };
}

function ReservationsScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const isAdmin = session.roles.includes("admin");

  const view = search.view || "week";
  const anchor = search.date ? parseLocal(`${search.date}T00:00:00`) : new Date();
  const { from, to } = computeRange(view, anchor);

  const resourcesQuery = useQuery({
    queryKey: reservationKeys.resources,
    queryFn: listResources,
  });
  const reservationsQuery = useQuery({
    queryKey: reservationKeys.list(from, to),
    queryFn: () => listReservations({ from, to }),
    placeholderData: keepPreviousData,
  });
  const showOutlook = search.outlook === "1";
  const myCalendarQuery = useQuery({
    queryKey: reservationKeys.myCalendar(from, to),
    queryFn: () => listMyCalendar({ from, to }),
    enabled: showOutlook,
    placeholderData: keepPreviousData,
  });

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  return (
    <div className="wl-page" data-screen-label="Back-office Reservations">
      <ReservationsClient
        resources={resourcesQuery.data ?? []}
        reservations={reservationsQuery.data?.data ?? []}
        unavailableRooms={reservationsQuery.data?.unavailable ?? []}
        outlookEvents={showOutlook ? myCalendarQuery.data ?? [] : []}
        search={search}
        updateParams={updateParams}
        isFetching={reservationsQuery.isFetching || (showOutlook && myCalendarQuery.isFetching)}
        isLoading={resourcesQuery.isLoading || reservationsQuery.isLoading}
        isAdmin={isAdmin}
      />
    </div>
  );
}
